import { useState, useEffect, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import QRCode from 'qrcode'
import { api } from '../api/axios'
import { useAuth } from '../contexts/AuthContext'
import { DateInput, toISODate } from '../components/ui/DateInput'
import CampoSenha from '../components/CampoSenha'

// ── Types ────────────────────────────────────────────────────────────────────

interface Atirador {
  idatirador: number
  nome: string
  cpf: string
  email: string | null
  clube_nome: string
  data_nascimento: string
}

interface Clube {
  idclube: number
  nome: string
  cidade: string
  estado: string
}

interface Campeonato {
  idcampeonato: number
  descricao: string
  temporada: number
  clube_nome: string
}

interface Modalidade {
  idmodalidade: number
  nome: string
  grupo: string
  divisao: string | null
  permite_reinscricao: boolean
  max_reinscricao: number
}

interface EtapaInfo {
  idetapa: number
  idmodalidade: number
  titulo: string
  titulo_reduzido: string
  numero_etapa: number
  data_inicio: string
  data_encerramento: string
  data_encerramento_inscricoes: string
  campeonato_descricao: string
  modalidade_nome: string
}

interface RegraValor {
  operador: string
  quantidade: number
  valor: number
  ordem_aplicacao: number
}

interface RegraValoresResponse {
  numero_max_inscricao: number
  regras: RegraValor[]
}

interface EtapaRound {
  numero_etapa: number
  titulo: string
  data_encerramento_min: string
  modalidades_abertas: number
}

interface ModalidadeComEtapa extends Modalidade {
  etapaAberta: EtapaInfo
}

interface BatchResult {
  inscricoes: Array<{
    idmodalidade: number
    modalidade_nome: string
    quantidade: number
    subtotal: number
  }>
  total: number
}

interface CartaoForm {
  numero: string
  nome: string
  validade: string
  cvv: string
  cpf_titular: string
  parcelas: number
}

type Step = 'busca' | 'cadastro' | 'selecionado' | 'selecionar-modalidades' | 'resumo' | 'pagamento' | 'sucesso'
type PaymentMode = 'none' | 'pix' | 'cartao' | 'pos' | 'isento'

// ── PIX Constants ─────────────────────────────────────────────────────────────

const PIX_KEY = '2ed7db2c-decd-4604-9b2e-2d9ffdcc911d'
const PIX_NOME = 'CLUBE DE TIRO AGUIA'
const PIX_CIDADE = 'CUIABA'
const WHATSAPP_URL = 'https://wa.me/5566992107472?text=Segue%20comprovante%20do%20PIX%20da%20inscricao'

// ── PIX Payload Generator (EMV/BRCode Static) ─────────────────────────────────

function crc16ccitt(str: string): string {
  let crc = 0xffff
  for (let i = 0; i < str.length; i++) {
    crc ^= str.charCodeAt(i) << 8
    for (let j = 0; j < 8; j++) {
      if (crc & 0x8000) {
        crc = ((crc << 1) ^ 0x1021) & 0xffff
      } else {
        crc = (crc << 1) & 0xffff
      }
    }
  }
  return crc.toString(16).toUpperCase().padStart(4, '0')
}

function emvField(id: string, value: string): string {
  return `${id}${value.length.toString().padStart(2, '0')}${value}`
}

function generatePixPayload(valor: number): string {
  const merchantAccount =
    emvField('00', 'br.gov.bcb.pix') +
    emvField('01', PIX_KEY)

  const additionalData = emvField('62', emvField('05', '***'))

  const nomeTrunc = PIX_NOME.substring(0, 25)
  const cidadeTrunc = PIX_CIDADE.substring(0, 15)
  const valorStr = valor.toFixed(2)

  const payload =
    emvField('00', '01') +
    emvField('26', merchantAccount) +
    emvField('52', '0000') +
    emvField('53', '986') +
    emvField('54', valorStr) +
    emvField('58', 'BR') +
    emvField('59', nomeTrunc) +
    emvField('60', cidadeTrunc) +
    additionalData +
    '6304'

  return payload + crc16ccitt(payload)
}

// ── API helpers ───────────────────────────────────────────────────────────────

const publicApi = {
  buscar: (q: string) =>
    api.get<{ data: Atirador[] }>('/publico/atiradores', { params: { q } }).then(r => r.data.data),
  clubes: () =>
    api.get<{ data: Clube[] }>('/publico/clubes').then(r => r.data.data),
  campeonatos: () =>
    api.get<{ data: Campeonato[] }>('/publico/campeonatos').then(r => r.data.data),
  modalidades: (idcampeonato: number) =>
    api.get<{ data: Modalidade[] }>('/publico/modalidades', { params: { idcampeonato } }).then(r => r.data.data),
  etapas: (idcampeonato: number) =>
    api.get<{ data: EtapaInfo[] }>('/publico/etapas', { params: { idcampeonato } }).then(r => r.data.data),
  cadastrar: (data: Record<string, unknown>) =>
    api.post<{ data: Atirador }>('/publico/atiradores', data).then(r => r.data.data),
  regraValores: (idcampeonato: number) =>
    api.get<RegraValoresResponse>(`/publico/campeonatos/${idcampeonato}/regra-valores`).then(r => r.data),
  inscricoesBatch: (data: {
    idatirador: number
    idcampeonato: number
    numero_etapa: number
    inscricoes: Array<{ idmodalidade: number; quantidade: number }>
    pagar?: boolean
    forma_pagamento?: string
  }) =>
    api.post<{ data: BatchResult }>('/publico/inscricoes-batch', data).then(r => r.data.data),
  pagbankCheckout: (data: {
    idatirador: number
    idcampeonato: number
    numero_etapa: number
    inscricoes: Array<{ idmodalidade: number; quantidade: number }>
    pagador: { nome: string; email: string; cpf: string }
    card: { encrypted: string; holder_name: string }
    parcelas: number
    valor_com_juros: number
  }) =>
    api.post<{ data: BatchResult; charge_status: string; pagbank_order_id: string; pagbank_charge_id: string }>(
      '/publico/pagbank/checkout',
      data,
    ).then(r => r.data),
  inscricoesAtirador: (idcampeonato: number, idatirador: number, numero_etapa: number) =>
    api.get<{ total_inscricoes: number }>(
      `/publico/campeonatos/${idcampeonato}/inscricoes-atirador`,
      { params: { idatirador, numero_etapa } },
    ).then(r => r.data.total_inscricoes),
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCpf(v: string) {
  return v.replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
}

function formatCurrency(v: number) {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

// ── Juros de parcelas ─────────────────────────────────────────────────────────
// 1x: 0,58%  |  2x e 3x: 4,99%
const TAXA_JUROS_1X = 0.0058
const TAXA_JUROS_23X = 0.0499
const MAX_PARCELAS = 3

function calcularValorComJuros(total: number, parcelas: number): number {
  if (parcelas === 1) return total * (1 + TAXA_JUROS_1X)
  return total * (1 + TAXA_JUROS_23X)
}

function calcularParcelasOpcoes(total: number): Array<{ n: number; totalComJuros: number; valorParcela: number; label: string }> {
  return Array.from({ length: MAX_PARCELAS }, (_, i) => {
    const n = i + 1
    const totalComJuros = calcularValorComJuros(total, n)
    const valorParcela = totalComJuros / n
    const jurosLabel = n === 1 ? '0,58% juros' : '4,99% juros'
    return {
      n,
      totalComJuros,
      valorParcela,
      label: `${n}x de ${formatCurrency(valorParcela)} (${jurosLabel})`,
    }
  })
}

function calcularValorInscricao(numero: number, regras: RegraValor[]): number {
  for (const regra of regras) {
    let match = false
    switch (regra.operador) {
      case '=':  match = numero === regra.quantidade; break
      case '>':  match = numero > regra.quantidade;   break
      case '>=': match = numero >= regra.quantidade;  break
      case '<':  match = numero < regra.quantidade;   break
      case '<=': match = numero <= regra.quantidade;  break
    }
    if (match) return Number(regra.valor)
  }
  return 0
}

function calcularSubtotal(quantidade: number, regras: RegraValor[], offset = 0): number {
  let total = 0
  for (let i = 1; i <= quantidade; i++) total += calcularValorInscricao(offset + i, regras)
  return total
}

function maskCardNumber(v: string): string {
  return v.replace(/\D/g, '').slice(0, 16).replace(/(\d{4})(?=\d)/g, '$1 ')
}

function maskValidade(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 4)
  if (d.length > 2) return d.slice(0, 2) + '/' + d.slice(2)
  return d
}

function maskCpf(v: string): string {
  return v.replace(/\D/g, '').slice(0, 11).replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
}

// ── Design tokens ──────────────────────────────────────────────────────────────
// Identidade emprestada da logo do clube (medalhao preto/branco): fundo quase
// preto, texto marfim, um unico acento em latao (cor de cartucho/trofeu) em
// vez do amarelo/azul genericos de antes.

const displayFont = { fontFamily: "'Oswald', 'Arial Narrow', sans-serif" } as const

const PANEL = 'bg-[#151519] border border-white/10 rounded-2xl p-5 sm:p-6'
const INPUT = 'w-full bg-black/30 border border-white/15 text-white placeholder-white/30 rounded-lg px-3.5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#caa14d]/60 focus:border-[#caa14d]/60 transition-colors'
const SELECT = 'w-full bg-[#1c1c22] border border-white/15 text-white rounded-lg px-3.5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#caa14d]/60 focus:border-[#caa14d]/60 transition-colors'
const LABEL = 'block text-[11px] uppercase tracking-wide text-white/40 font-medium mb-1.5'
const BTN_PRIMARY = 'bg-[#caa14d] hover:bg-[#caa14d] text-[#100f0c] font-bold rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed'
const BTN_GHOST = 'border border-white/15 text-white/60 hover:text-white hover:border-white/30 rounded-xl transition-colors'

// ── Icons ──────────────────────────────────────────────────────────────────────

function IconSearch({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  )
}

function IconArrowLeft({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
    </svg>
  )
}

function IconClose({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}

function IconCheck({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

function IconBolt({ className = 'w-7 h-7' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z" />
    </svg>
  )
}

function IconCard({ className = 'w-7 h-7' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2.5" y="5.5" width="19" height="13" rx="2" /><line x1="2.5" y1="10" x2="21.5" y2="10" />
    </svg>
  )
}

function IconDevice({ className = 'w-7 h-7' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="2.5" width="14" height="19" rx="2" /><line x1="9" y1="18" x2="15" y2="18" />
    </svg>
  )
}

function IconUnlock({ className = 'w-7 h-7' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="10.5" width="16" height="10" rx="2" /><path d="M7 10.5V7a5 5 0 0 1 9-3" />
    </svg>
  )
}

function IconWhatsApp({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  )
}

// ── Stepper ────────────────────────────────────────────────────────────────────

const STEP_LABELS = ['Identificação', 'Evento', 'Modalidades', 'Resumo', 'Pagamento']

function currentStepNumber(step: Step): number {
  switch (step) {
    case 'busca':
    case 'cadastro': return 1
    case 'selecionado': return 2
    case 'selecionar-modalidades': return 3
    case 'resumo': return 4
    case 'pagamento': return 5
    default: return 5
  }
}

function Stepper({ current }: { current: number }) {
  return (
    <ol className="flex items-center mb-8">
      {STEP_LABELS.map((label, i) => {
        const n = i + 1
        const done = n < current
        const active = n === current
        return (
          <li key={label} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center gap-1.5">
              <div className={[
                'w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-bold border shrink-0 transition-colors',
                done ? 'bg-[#caa14d] text-[#100f0c] border-[#caa14d]'
                  : active ? 'border-[#caa14d] text-[#caa14d] bg-[#caa14d]/10'
                  : 'border-white/15 text-white/25',
              ].join(' ')}>
                {done ? <IconCheck className="w-3.5 h-3.5" /> : n}
              </div>
              <span className={[
                'hidden sm:block text-[10px] uppercase tracking-wide font-semibold whitespace-nowrap',
                active ? 'text-[#caa14d]' : done ? 'text-white/50' : 'text-white/20',
              ].join(' ')}>
                {label}
              </span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div className={`flex-1 h-px mx-1.5 sm:mx-2 ${n < current ? 'bg-[#caa14d]/60' : 'bg-compumaq-pretoSuave/10'}`} />
            )}
          </li>
        )
      })}
    </ol>
  )
}

function PanelHeading({ n, children }: { n?: number; children: React.ReactNode }) {
  return (
    <h3 className="flex items-center gap-2.5 text-white font-semibold mb-4">
      {n != null && (
        <span className="w-6 h-6 rounded-full bg-[#caa14d]/15 text-[#caa14d] text-xs font-bold flex items-center justify-center shrink-0">
          {n}
        </span>
      )}
      {children}
    </h3>
  )
}

// ── Component ─────────────────────────────────────────────────────────────────

export function InscricaoPublicaPage() {
  const { usuario } = useAuth()
  const isAdmin = usuario?.roles.includes('admin') ?? false
  const [searchParams] = useSearchParams()
  const autoSelecionouCampeonato = useRef(false)
  const autoSelecionouEtapa = useRef(false)

  const [step, setStep] = useState<Step>('busca')
  const [busca, setBusca] = useState('')
  const [resultados, setResultados] = useState<Atirador[]>([])
  const [atirador, setAtirador] = useState<Atirador | null>(null)
  const [clubes, setClubes] = useState<Clube[]>([])
  const [campeonatos, setCampeonatos] = useState<Campeonato[]>([])
  const [modalidades, setModalidades] = useState<Modalidade[]>([])
  const [etapasAll, setEtapasAll] = useState<EtapaInfo[]>([])
  const [selCampeonato, setSelCampeonato] = useState<Campeonato | null>(null)
  const [selNumeroEtapa, setSelNumeroEtapa] = useState<number | null>(null)
  const [regraValores, setRegraValores] = useState<RegraValoresResponse | null>(null)
  const [quantidades, setQuantidades] = useState<Record<number, number>>({})
  const [existingInscricoesCount, setExistingInscricoesCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [batchResult, setBatchResult] = useState<BatchResult | null>(null)

  // Payment state
  const [paymentMode, setPaymentMode] = useState<PaymentMode>('none')
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null)
  const [pixCopiado, setPixCopiado] = useState(false)

  // Cartao state
  const [cartaoForm, setCartaoForm] = useState<CartaoForm>({
    numero: '', nome: '', validade: '', cvv: '', cpf_titular: '', parcelas: 1,
  })
  const [cartaoLoading, setCartaoLoading] = useState(false)
  const [cartaoErro, setCartaoErro] = useState<string | null>(null)
  const [cartaoAprovado, setCartaoAprovado] = useState(false)
  const [posIsento, setPosIsento] = useState(false)

  const [novoAtirador, setNovoAtirador] = useState({
    nome: '', cpf: '', data_nascimento: '', email: '', telefone: '', numero_cr: '', idclube: 0, senha: '',
  })

  // ── Computed values ──────────────────────────────────────────────────────────

  // Etapa atual: somente a 2a etapa (numero_etapa >= 2) aparece para novas inscricoes
  const ETAPA_MINIMA_INSCRICAO = 2

  const etapaRounds: EtapaRound[] = (() => {
    const now = new Date()
    const groups: Record<number, EtapaInfo[]> = {}
    for (const e of etapasAll) {
      if (e.numero_etapa < ETAPA_MINIMA_INSCRICAO) continue
      if (!groups[e.numero_etapa]) groups[e.numero_etapa] = []
      groups[e.numero_etapa].push(e)
    }
    return Object.entries(groups)
      .map(([num, etps]) => {
        const abertas = etps.filter(e => new Date(e.data_encerramento_inscricoes) > now)
        const datas = etps.map(e => e.data_encerramento_inscricoes).sort()
        return {
          numero_etapa: parseInt(num, 10),
          titulo: `Etapa ${num}`,
          data_encerramento_min: datas[0],
          modalidades_abertas: abertas.length,
        }
      })
      .filter(r => r.modalidades_abertas > 0)
      .sort((a, b) => a.numero_etapa - b.numero_etapa)
  })()

  const modalidadesComEtapa: ModalidadeComEtapa[] = (() => {
    if (!selNumeroEtapa) return []
    const now = new Date()
    const result: ModalidadeComEtapa[] = []
    for (const m of modalidades) {
      const etapa = etapasAll.find(
        e => e.idmodalidade === m.idmodalidade &&
             e.numero_etapa === selNumeroEtapa &&
             new Date(e.data_encerramento_inscricoes) > now,
      )
      if (etapa) result.push({ ...m, etapaAberta: etapa })
    }
    return result
  })()

  const { subtotaisGlobais, totalGeral } = (() => {
    const subtotais: Record<number, number> = {}
    if (!regraValores) return { subtotaisGlobais: subtotais, totalGeral: 0 }
    let globalOffset = existingInscricoesCount
    let total = 0
    for (const m of modalidadesComEtapa) {
      const qty = quantidades[m.idmodalidade] ?? 0
      if (qty === 0) { subtotais[m.idmodalidade] = 0; continue }
      const sub = calcularSubtotal(qty, regraValores.regras, globalOffset)
      subtotais[m.idmodalidade] = sub
      globalOffset += qty
      total += sub
    }
    return { subtotaisGlobais: subtotais, totalGeral: total }
  })()

  const temSelecionado = Object.values(quantidades).some(q => q > 0)

  // ── Handlers ─────────────────────────────────────────────────────────────────

  async function handleBuscar(e: React.FormEvent) {
    e.preventDefault()
    if (!busca.trim() || busca.trim().length < 2) return
    setLoading(true)
    setError(null)
    try {
      const res = await publicApi.buscar(busca)
      setResultados(res)
    } catch {
      setError('Erro ao buscar atirador. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  async function selecionarAtirador(a: Atirador) {
    setAtirador(a)
    setStep('selecionado')
    setLoading(true)
    try {
      const camps = await publicApi.campeonatos()
      setCampeonatos(camps)
    } catch {
      setError('Erro ao carregar campeonatos.')
    } finally {
      setLoading(false)
    }
  }

  async function abrirCadastro() {
    setStep('cadastro')
    setLoading(true)
    try {
      const cs = await publicApi.clubes()
      setClubes(cs)
    } catch {
      setError('Erro ao carregar clubes.')
    } finally {
      setLoading(false)
    }
  }

  async function handleCadastrar(e: React.FormEvent) {
    e.preventDefault()
    if (!novoAtirador.nome || !novoAtirador.cpf || !novoAtirador.data_nascimento || !novoAtirador.idclube) {
      setError('Nome, CPF, data de nascimento e clube sao obrigatorios.')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const payload = {
        ...novoAtirador,
        data_nascimento: toISODate(novoAtirador.data_nascimento) || novoAtirador.data_nascimento,
      }
      const novo = await publicApi.cadastrar(payload)
      setAtirador(novo)
      const camps = await publicApi.campeonatos()
      setCampeonatos(camps)
      setStep('selecionado')
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Erro ao cadastrar. Verifique os dados.')
    } finally {
      setLoading(false)
    }
  }

  async function handleSelectCampeonato(c: Campeonato) {
    setSelCampeonato(c)
    setSelNumeroEtapa(null)
    setQuantidades({})
    setLoading(true)
    setError(null)
    try {
      const [mods, etps, regras] = await Promise.all([
        publicApi.modalidades(c.idcampeonato),
        publicApi.etapas(c.idcampeonato),
        publicApi.regraValores(c.idcampeonato),
      ])
      setModalidades(mods)
      setEtapasAll(etps)
      setRegraValores(regras)
    } catch {
      setError('Erro ao carregar dados do campeonato.')
    } finally {
      setLoading(false)
    }
  }

  function handleSelectNumeroEtapa(n: number) {
    setSelNumeroEtapa(n)
    setQuantidades({})
    setExistingInscricoesCount(0)
    setStep('selecionar-modalidades')
    // Buscar contagem de inscricoes existentes do atirador nesta etapa
    if (atirador && selCampeonato) {
      publicApi.inscricoesAtirador(selCampeonato.idcampeonato, atirador.idatirador, n)
        .then(count => setExistingInscricoesCount(count))
        .catch(() => { /* nao bloqueia o fluxo */ })
    }
  }

  // Deep-link: /inscricao-publica?idcampeonato=X&numero_etapa=Y pre-seleciona
  // o campeonato e a etapa depois que o atirador se identifica (CPF), pulando
  // a selecao manual das duas primeiras etapas do wizard.
  useEffect(() => {
    if (autoSelecionouCampeonato.current) return
    if (!atirador || campeonatos.length === 0) return
    const idParam = searchParams.get('idcampeonato')
    if (!idParam) { autoSelecionouCampeonato.current = true; return }
    const idcampeonato = parseInt(idParam, 10)
    const campeonato = campeonatos.find(c => c.idcampeonato === idcampeonato)
    autoSelecionouCampeonato.current = true
    if (campeonato) handleSelectCampeonato(campeonato)
  }, [atirador, campeonatos, searchParams])

  useEffect(() => {
    if (autoSelecionouEtapa.current) return
    if (!selCampeonato || etapaRounds.length === 0) return
    const numeroParam = searchParams.get('numero_etapa')
    if (!numeroParam) { autoSelecionouEtapa.current = true; return }
    const numero = parseInt(numeroParam, 10)
    const round = etapaRounds.find(r => r.numero_etapa === numero)
    autoSelecionouEtapa.current = true
    if (round) handleSelectNumeroEtapa(numero)
  }, [selCampeonato, etapaRounds, searchParams])

  function getMaxQty(m: Modalidade): number {
    if (!regraValores) return 0
    const maxInscricao = regraValores.numero_max_inscricao
    if (!m.permite_reinscricao) return 1
    const maxMod = m.max_reinscricao > 0 ? m.max_reinscricao : maxInscricao
    return Math.min(maxInscricao, maxMod)
  }

  function adjustQty(idmodalidade: number, delta: number, maxQty: number) {
    setQuantidades(prev => {
      const current = prev[idmodalidade] ?? 0
      const next = Math.max(0, Math.min(maxQty, current + delta))
      return { ...prev, [idmodalidade]: next }
    })
  }

  function irParaResumo() {
    if (!temSelecionado) {
      setError('Selecione pelo menos uma modalidade.')
      return
    }
    setError(null)
    setStep('resumo')
  }

  async function handleEscolherPix() {
    // Cria a inscricao como pendente_pagamento ANTES de mostrar o QR
    if (!atirador || !selCampeonato || !selNumeroEtapa) return
    const inscricoes = Object.entries(quantidades)
      .filter(([, qty]) => qty > 0)
      .map(([idmod, qty]) => ({ idmodalidade: parseInt(idmod, 10), quantidade: qty }))
    if (inscricoes.length === 0) return
    setLoading(true)
    setError(null)
    try {
      const result = await publicApi.inscricoesBatch({
        idatirador: atirador.idatirador,
        idcampeonato: selCampeonato.idcampeonato,
        numero_etapa: selNumeroEtapa,
        inscricoes,
        pagar: false,
      })
      setBatchResult(result)
      setPaymentMode('pix')
      setQrCodeDataUrl(null)
      setPixCopiado(false)
      setStep('pagamento')
      // Gerar QR Code apos criar inscricao
      try {
        const payload = generatePixPayload(result.total)
        const dataUrl = await QRCode.toDataURL(payload, {
          width: 280,
          margin: 2,
          color: { dark: '#000000', light: '#ffffff' },
        })
        setQrCodeDataUrl(dataUrl)
      } catch {
        // QR code error nao bloqueia o fluxo
      }
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Erro ao realizar inscricao. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  function handleEscolherCartao() {
    setPaymentMode('cartao')
    setCartaoErro(null)
    setCartaoAprovado(false)
    setCartaoForm({ numero: '', nome: '', validade: '', cvv: '', cpf_titular: '', parcelas: 1 })
    setStep('pagamento')
  }

  async function copiarChavePix() {
    try {
      await navigator.clipboard.writeText(PIX_KEY)
      setPixCopiado(true)
      setTimeout(() => setPixCopiado(false), 2500)
    } catch {
      setPixCopiado(false)
    }
  }

  async function handleCartaoSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!atirador || !selCampeonato || !selNumeroEtapa) return

    setCartaoErro(null)
    setCartaoLoading(true)

    try {
      const PagSeguro = (window as any).PagSeguro
      if (!PagSeguro) {
        setCartaoErro('SDK do PagBank nao carregado. Recarregue a pagina.')
        setCartaoLoading(false)
        return
      }

      const [expMonth, expYear] = cartaoForm.validade.split('/')
      const cardResult = PagSeguro.encryptCard({
        publicKey: import.meta.env.VITE_PAGBANK_PUBLIC_KEY as string,
        holder: cartaoForm.nome,
        number: cartaoForm.numero.replace(/\s/g, ''),
        expMonth: expMonth ?? '',
        expYear: expYear ? (expYear.length === 2 ? '20' + expYear : expYear) : '',
        securityCode: cartaoForm.cvv,
      })

      if (cardResult.hasErrors) {
        const msgs = (cardResult.errors as Array<{ message: string }>).map(er => er.message).join('; ')
        setCartaoErro('Dados do cartao invalidos: ' + msgs)
        setCartaoLoading(false)
        return
      }

      const inscricoes = Object.entries(quantidades)
        .filter(([, qty]) => qty > 0)
        .map(([idmod, qty]) => ({ idmodalidade: parseInt(idmod, 10), quantidade: qty }))

      const valorComJuros = calcularValorComJuros(totalGeral, cartaoForm.parcelas)

      const resp = await publicApi.pagbankCheckout({
        idatirador: atirador.idatirador,
        idcampeonato: selCampeonato.idcampeonato,
        numero_etapa: selNumeroEtapa,
        inscricoes,
        pagador: {
          nome: atirador.nome,
          email: atirador.email ?? `${atirador.cpf.replace(/\D/g, '')}@inscricao.local`,
          cpf: atirador.cpf,
        },
        card: {
          encrypted: cardResult.encryptedCard,
          holder_name: cartaoForm.nome,
        },
        parcelas: cartaoForm.parcelas,
        valor_com_juros: valorComJuros,
      })

      setCartaoAprovado(true)
      setBatchResult(resp.data)
      setStep('sucesso')
    } catch (err: any) {
      const msg = err?.response?.data?.error ?? 'Erro ao processar pagamento. Tente novamente.'
      setCartaoErro(msg)
    } finally {
      setCartaoLoading(false)
    }
  }

  async function handleFinalizarInscricao(pagar = false, forma_pagamento?: string) {
    if (!atirador || !selCampeonato || !selNumeroEtapa) return
    const inscricoes = Object.entries(quantidades)
      .filter(([, qty]) => qty > 0)
      .map(([idmod, qty]) => ({ idmodalidade: parseInt(idmod, 10), quantidade: qty }))
    if (inscricoes.length === 0) return
    setLoading(true)
    setError(null)
    try {
      const result = await publicApi.inscricoesBatch({
        idatirador: atirador.idatirador,
        idcampeonato: selCampeonato.idcampeonato,
        numero_etapa: selNumeroEtapa,
        inscricoes,
        pagar,
        forma_pagamento,
      })
      setBatchResult(result)
      if (forma_pagamento === 'pos' || forma_pagamento === 'isento') {
        setPosIsento(true)
      }
      setStep('sucesso')
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Erro ao realizar inscricao. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  function imprimirComprovante() {
    if (!batchResult || !atirador || !selCampeonato) return
    const formaPagto = cartaoAprovado ? 'Cartao de Credito' : posIsento ? (paymentMode === 'isento' ? 'Isento' : 'Cartao POS') : 'PIX'
    const linhasModalidades = batchResult.inscricoes.map(insc =>
      `<tr>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;">${insc.modalidade_nome}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:center;">${insc.quantidade}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;">${formatCurrency(insc.subtotal)}</td>
      </tr>`
    ).join('')
    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8"/>
  <title>Comprovante de Inscricao</title>
  <style>
    body { font-family: Arial, sans-serif; font-size: 13px; color: #111; margin: 32px; }
    h1 { font-size: 18px; margin-bottom: 4px; }
    .subtitle { color: #555; font-size: 12px; margin-bottom: 20px; }
    .info { margin-bottom: 16px; }
    .info-row { display: flex; gap: 16px; margin-bottom: 6px; }
    .label { font-weight: bold; min-width: 100px; }
    table { width: 100%; border-collapse: collapse; margin-top: 12px; }
    thead { background: #f3f4f6; }
    th { padding: 8px; text-align: left; font-size: 12px; border-bottom: 2px solid #d1d5db; }
    .total-row td { font-weight: bold; padding-top: 10px; font-size: 14px; }
    .footer { margin-top: 24px; font-size: 11px; color: #888; border-top: 1px solid #e5e7eb; padding-top: 10px; }
  </style>
</head>
<body>
  <h1>Comprovante de Inscricao</h1>
  <div class="subtitle">Campeonato de Tiro</div>
  <div class="info">
    <div class="info-row"><span class="label">Atirador:</span><span>${atirador.nome}</span></div>
    <div class="info-row"><span class="label">CPF:</span><span>${atirador.cpf}</span></div>
    <div class="info-row"><span class="label">Clube:</span><span>${atirador.clube_nome}</span></div>
    <div class="info-row"><span class="label">Campeonato:</span><span>${selCampeonato.descricao}</span></div>
    <div class="info-row"><span class="label">Etapa:</span><span>${selNumeroEtapa}</span></div>
    <div class="info-row"><span class="label">Pagamento:</span><span>${formaPagto}</span></div>
  </div>
  <table>
    <thead>
      <tr>
        <th>Modalidade</th>
        <th style="text-align:center;">Qtd</th>
        <th style="text-align:right;">Valor</th>
      </tr>
    </thead>
    <tbody>
      ${linhasModalidades}
      <tr class="total-row">
        <td colspan="2" style="padding:10px 8px 0;">Total</td>
        <td style="padding:10px 8px 0;text-align:right;">${formatCurrency(batchResult.total)}</td>
      </tr>
    </tbody>
  </table>
  <div class="footer">Emitido em ${new Date().toLocaleString('pt-BR')}</div>
</body>
</html>`
    const win = window.open('', '_blank', 'width=700,height=600')
    if (win) {
      win.document.write(html)
      win.document.close()
      win.focus()
      setTimeout(() => win.print(), 400)
    }
  }

  function resetar() {
    setStep('busca')
    setBusca('')
    setResultados([])
    setAtirador(null)
    setSelCampeonato(null)
    setSelNumeroEtapa(null)
    setQuantidades({})
    setExistingInscricoesCount(0)
    setBatchResult(null)
    setPaymentMode('none')
    setQrCodeDataUrl(null)
    setPixCopiado(false)
    setCartaoErro(null)
    setCartaoAprovado(false)
    setPosIsento(false)
    setCartaoForm({ numero: '', nome: '', validade: '', cvv: '', cpf_titular: '', parcelas: 1 })
    setError(null)
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#0b0b0d] text-white flex flex-col relative">
      {/* Vinheta sutil de fundo — sem gradiente generico de "hero" */}
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_at_top,rgba(200,161,60,0.07),transparent_55%)]" />

      {/* Header */}
      <header className="relative z-10 border-b border-white/10">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 min-w-0">
            <img
              src="/sponsors/logo-aguia.jpeg"
              alt="Clube de Tiro Águia"
              className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover ring-2 ring-[#caa14d]/70 shrink-0"
              onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
            />
            <div className="min-w-0">
              <p style={displayFont} className="text-white text-base sm:text-lg uppercase tracking-wide leading-tight truncate">
                Campeonato 2026 Águia
              </p>
              <p className="text-[#caa14d] text-xs font-semibold tracking-wide">
                2ª Etapa &middot; 29&ndash;30 de agosto de 2026
              </p>
            </div>
          </Link>
          <Link
            to="/login"
            className="hidden sm:inline-flex text-xs text-white/50 hover:text-white border border-white/15 hover:border-white/30 px-3 py-2 rounded-lg transition-colors shrink-0"
          >
            Entrar no sistema
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="relative z-10 flex-1 px-4 sm:px-6 py-8 sm:py-10">
        <div className="w-full max-w-2xl mx-auto">
          {step !== 'sucesso' && <Stepper current={currentStepNumber(step)} />}

          {(step === 'busca' || step === 'cadastro') && (
            <div className="text-center mb-7">
              <h1 style={displayFont} className="text-3xl sm:text-4xl uppercase tracking-wide text-white mb-2">
                Inscrição Online
              </h1>
              <p className="text-white/45 text-sm">Encontre seu cadastro ou crie um novo para se inscrever</p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mb-4 bg-red-950/50 border border-red-500/30 rounded-xl p-4 text-red-300 text-sm flex justify-between items-start gap-3">
              <span>{error}</span>
              <button className="text-red-400 hover:text-red-200 shrink-0" onClick={() => setError(null)}>
                <IconClose />
              </button>
            </div>
          )}

          {/* ── STEP: BUSCA ──────────────────────────────────────────────────── */}
          {step === 'busca' && (
            <div className={PANEL}>
              <PanelHeading><IconSearch className="w-4 h-4 text-[#caa14d]" /> Buscar por CPF ou nome</PanelHeading>
              <form onSubmit={handleBuscar} className="flex gap-3 mb-4">
                <input
                  className={INPUT}
                  placeholder="Digite CPF (ex: 123.456.789-00) ou nome..."
                  value={busca}
                  onChange={e => setBusca(e.target.value)}
                />
                <button type="submit" disabled={loading} className={`${BTN_PRIMARY} px-5 py-3 text-sm shrink-0`}>
                  {loading ? '...' : 'Buscar'}
                </button>
              </form>

              {resultados.length > 0 && (
                <div className="space-y-2">
                  {resultados.map(a => (
                    <button
                      key={a.idatirador}
                      onClick={() => selecionarAtirador(a)}
                      className="w-full text-left bg-compumaq-pretoSuave/[0.03] hover:bg-compumaq-pretoSuave/[0.07] border border-white/10 hover:border-[#caa14d]/40 rounded-xl p-4 transition-colors"
                    >
                      <p className="text-white font-semibold">{a.nome}</p>
                      <p className="text-white/40 text-xs mt-0.5">{formatCpf(a.cpf)} &middot; {a.clube_nome}</p>
                    </button>
                  ))}
                </div>
              )}

              {resultados.length === 0 && busca && !loading && (
                <div className="text-center py-6 border-t border-white/10 mt-2 pt-6">
                  <p className="text-white/40 mb-4 text-sm">Atirador não encontrado.</p>
                  <button onClick={abrirCadastro} className={`${BTN_PRIMARY} px-6 py-3 text-sm`}>
                    + Cadastrar Novo Atirador
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── STEP: CADASTRO ───────────────────────────────────────────────── */}
          {step === 'cadastro' && (
            <div className={PANEL}>
              <div className="flex items-center gap-3 mb-5">
                <button onClick={() => setStep('busca')} className="text-white/40 hover:text-white flex items-center gap-1 text-sm">
                  <IconArrowLeft /> Voltar
                </button>
                <h2 className="text-white font-semibold text-lg">Novo Atirador</h2>
              </div>
              <form onSubmit={handleCadastrar} className="space-y-3.5">
                <div className="grid grid-cols-2 gap-3.5">
                  <div className="col-span-2">
                    <label className={LABEL}>Nome completo *</label>
                    <input
                      className={INPUT}
                      value={novoAtirador.nome} onChange={e => setNovoAtirador(p => ({ ...p, nome: e.target.value }))}
                      placeholder="Nome" required
                    />
                  </div>
                  <div>
                    <label className={LABEL}>CPF *</label>
                    <input
                      className={INPUT}
                      value={novoAtirador.cpf} onChange={e => setNovoAtirador(p => ({ ...p, cpf: e.target.value }))}
                      placeholder="000.000.000-00" required
                    />
                  </div>
                  <div>
                    <label className={LABEL}>Data de Nascimento *</label>
                    <DateInput
                      value={novoAtirador.data_nascimento}
                      onChange={val => setNovoAtirador(p => ({ ...p, data_nascimento: val }))}
                      className={INPUT}
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <label className={LABEL}>Clube *</label>
                    <select
                      className={SELECT}
                      value={novoAtirador.idclube} onChange={e => setNovoAtirador(p => ({ ...p, idclube: parseInt(e.target.value, 10) }))}
                      required
                    >
                      <option value={0}>Selecione o clube...</option>
                      {clubes.map(c => (
                        <option key={c.idclube} value={c.idclube}>{c.nome} - {c.cidade}/{c.estado}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={LABEL}>E-mail</label>
                    <input
                      type="email"
                      className={INPUT}
                      value={novoAtirador.email} onChange={e => setNovoAtirador(p => ({ ...p, email: e.target.value }))}
                      placeholder="seu@email.com"
                    />
                  </div>
                  <div>
                    <label className={LABEL}>Telefone</label>
                    <input
                      className={INPUT}
                      value={novoAtirador.telefone} onChange={e => setNovoAtirador(p => ({ ...p, telefone: e.target.value }))}
                      placeholder="(65) 99999-9999"
                    />
                  </div>
                  <div>
                    <label className={LABEL}>N. CR</label>
                    <input
                      className={INPUT}
                      value={novoAtirador.numero_cr} onChange={e => setNovoAtirador(p => ({ ...p, numero_cr: e.target.value }))}
                      placeholder="Numero do CR"
                    />
                  </div>
                  <div>
                    <label className={LABEL}>Senha de acesso</label>
                    <CampoSenha
                      className={INPUT}
                      iconeClassName="text-white/40 hover:text-white"
                      value={novoAtirador.senha} onChange={e => setNovoAtirador(p => ({ ...p, senha: e.target.value }))}
                      placeholder="Deixe em branco = usar CPF"
                    />
                  </div>
                </div>
                <p className="text-white/30 text-xs">
                  Se informar e-mail, sera criado um acesso para acompanhar inscricoes. A senha padrao e o CPF (somente numeros).
                </p>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button" onClick={() => setStep('busca')}
                    className={`${BTN_GHOST} flex-1 py-3 text-sm`}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit" disabled={loading}
                    className={`${BTN_PRIMARY} flex-1 py-3 text-sm`}
                  >
                    {loading ? 'Cadastrando...' : 'Cadastrar'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ── STEP: SELECIONADO — campeonato + etapa ───────────────────────── */}
          {step === 'selecionado' && atirador && (
            <div className="space-y-4">
              {/* Card atirador */}
              <div className="bg-emerald-950/40 border border-emerald-500/25 rounded-2xl p-4 flex items-center gap-4">
                <div className="w-11 h-11 rounded-full bg-emerald-500/15 flex items-center justify-center text-emerald-400 font-bold text-lg shrink-0">
                  {atirador.nome.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold truncate">{atirador.nome}</p>
                  <p className="text-white/40 text-xs">{formatCpf(atirador.cpf)} &middot; {atirador.clube_nome}</p>
                </div>
                <button
                  onClick={() => { setAtirador(null); setStep('busca'); setBusca(''); setResultados([]) }}
                  className="text-white/35 hover:text-white text-xs shrink-0"
                >
                  Trocar
                </button>
              </div>

              {/* Selecionar campeonato */}
              <div className={PANEL}>
                <PanelHeading n={1}>Selecione o Campeonato</PanelHeading>
                {loading && <p className="text-white/40 text-sm">Carregando...</p>}
                <div className="space-y-2">
                  {campeonatos.map(c => (
                    <button
                      key={c.idcampeonato}
                      onClick={() => handleSelectCampeonato(c)}
                      className={`w-full text-left p-3.5 rounded-xl border transition-all text-sm ${
                        selCampeonato?.idcampeonato === c.idcampeonato
                          ? 'bg-[#caa14d]/15 border-[#caa14d]/50 text-[#e8c979]'
                          : 'bg-compumaq-pretoSuave/[0.03] border-white/10 text-white/70 hover:bg-compumaq-pretoSuave/[0.06]'
                      }`}
                    >
                      <span className="font-medium">{c.descricao}</span>
                      <span className="text-xs ml-2 opacity-60">{c.temporada} &middot; {c.clube_nome}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Selecionar etapa (round) */}
              {selCampeonato && !loading && (
                <div className={PANEL}>
                  <PanelHeading n={2}>Selecione a Etapa</PanelHeading>
                  {etapaRounds.length === 0 ? (
                    <p className="text-white/35 text-sm">Nenhuma etapa com inscricoes abertas para este campeonato.</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {etapaRounds.map(round => (
                        <button
                          key={round.numero_etapa}
                          onClick={() => handleSelectNumeroEtapa(round.numero_etapa)}
                          className={`p-3.5 rounded-xl border transition-all text-sm text-center ${
                            selNumeroEtapa === round.numero_etapa
                              ? 'bg-[#caa14d]/15 border-[#caa14d]/50 text-[#e8c979]'
                              : 'bg-compumaq-pretoSuave/[0.03] border-white/10 text-white/70 hover:bg-compumaq-pretoSuave/[0.06]'
                          }`}
                        >
                          <p className="font-semibold">{round.titulo}</p>
                          <p className="text-xs opacity-60 mt-0.5">
                            {round.modalidades_abertas} modalidade{round.modalidades_abertas !== 1 ? 's' : ''}
                          </p>
                          <p className="text-xs opacity-40 mt-0.5">
                            ate {new Date(round.data_encerramento_min).toLocaleDateString('pt-BR')}
                          </p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── STEP: SELECIONAR MODALIDADES ─────────────────────────────────── */}
          {step === 'selecionar-modalidades' && atirador && selCampeonato && (
            <div className="space-y-4">
              {/* Header info */}
              <div className={`${PANEL} p-4 sm:p-4`}>
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-white font-semibold truncate">{atirador.nome}</p>
                    <p className="text-[#caa14d] text-sm mt-0.5">
                      {selCampeonato.descricao} &mdash; Etapa {selNumeroEtapa}
                    </p>
                  </div>
                  <button
                    onClick={() => setStep('selecionado')}
                    className="text-white/40 hover:text-white text-xs shrink-0 ml-4 flex items-center gap-1"
                  >
                    <IconArrowLeft className="w-3.5 h-3.5" /> Voltar
                  </button>
                </div>
              </div>

              {/* Grid de modalidades */}
              <div className={PANEL}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-white font-semibold">Selecione as Modalidades</h3>
                    {regraValores && regraValores.regras.length > 0 && (
                      <p className="text-white/40 text-xs mt-1">
                        {existingInscricoesCount > 0 ? (
                          <span>
                            Ja possui {existingInscricoesCount} inscricao{existingInscricoesCount !== 1 ? 'oes' : ''} &mdash; novas inscricoes: {formatCurrency(calcularValorInscricao(2, regraValores.regras))} cada
                          </span>
                        ) : (
                          <>
                            1&ordf; inscricao: {formatCurrency(calcularValorInscricao(1, regraValores.regras))}
                            {regraValores.numero_max_inscricao > 1 && (
                              <span>
                                {' '}&bull; 2&ordf;+:{' '}
                                {formatCurrency(calcularValorInscricao(2, regraValores.regras))} cada
                              </span>
                            )}
                          </>
                        )}
                      </p>
                    )}
                  </div>
                  {temSelecionado && (
                    <span className="text-emerald-400 font-bold text-sm shrink-0 ml-4 tabular-nums">
                      {formatCurrency(totalGeral)}
                    </span>
                  )}
                </div>

                {modalidadesComEtapa.length === 0 ? (
                  <p className="text-white/35 text-sm text-center py-6">
                    Nenhuma modalidade disponivel para esta etapa.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {modalidadesComEtapa.map(m => {
                      const qty = quantidades[m.idmodalidade] ?? 0
                      const maxQty = getMaxQty(m)
                      const subtotal = subtotaisGlobais[m.idmodalidade] ?? 0
                      return (
                        <div
                          key={m.idmodalidade}
                          className={`rounded-xl border p-3 transition-all ${
                            qty > 0
                              ? 'bg-[#caa14d]/[0.08] border-[#caa14d]/30'
                              : 'bg-compumaq-pretoSuave/[0.03] border-white/10'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-sm font-medium truncate">{m.nome}</p>
                              <p className="text-white/40 text-xs">
                                {m.grupo}{m.divisao ? ` · ${m.divisao}` : ''}
                                {maxQty > 1 && (
                                  <span className="ml-1 opacity-60">— max {maxQty}x</span>
                                )}
                              </p>
                            </div>
                            {/* Valor + controles */}
                            <div className="flex items-center gap-2 shrink-0">
                              {qty > 0 && (
                                <span className="text-emerald-400 text-xs font-semibold min-w-[56px] text-right tabular-nums">
                                  {formatCurrency(subtotal)}
                                </span>
                              )}
                              <button
                                onClick={() => adjustQty(m.idmodalidade, -1, maxQty)}
                                disabled={qty === 0}
                                className="w-8 h-8 rounded-lg bg-compumaq-pretoSuave/[0.06] hover:bg-compumaq-pretoSuave/[0.12] text-white font-bold disabled:opacity-25 transition-colors flex items-center justify-center text-lg"
                              >
                                &minus;
                              </button>
                              <span className="text-white font-bold text-sm w-5 text-center select-none tabular-nums">{qty}</span>
                              <button
                                onClick={() => adjustQty(m.idmodalidade, +1, maxQty)}
                                disabled={qty >= maxQty}
                                className="w-8 h-8 rounded-lg bg-[#caa14d] hover:bg-[#caa14d] text-[#100f0c] font-bold disabled:opacity-25 transition-colors flex items-center justify-center text-lg"
                              >
                                +
                              </button>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* Total */}
                {totalGeral > 0 && (
                  <div className="mt-4 pt-4 border-t border-white/10 flex justify-between items-center">
                    <span className="text-white/70 font-semibold">Total</span>
                    <span style={displayFont} className="text-emerald-400 text-2xl tabular-nums">{formatCurrency(totalGeral)}</span>
                  </div>
                )}
              </div>

              <button
                onClick={irParaResumo}
                disabled={!temSelecionado}
                className={`${BTN_PRIMARY} w-full py-4 text-lg shadow-lg shadow-black/40`}
              >
                Continuar para Resumo
              </button>
            </div>
          )}

          {/* ── STEP: RESUMO ─────────────────────────────────────────────────── */}
          {step === 'resumo' && atirador && selCampeonato && (
            <div className="space-y-4">
              <div className={PANEL}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-semibold text-lg">Resumo da Inscricao</h3>
                  <button
                    onClick={() => setStep('selecionar-modalidades')}
                    className="text-white/40 hover:text-white text-xs flex items-center gap-1"
                  >
                    <IconArrowLeft className="w-3.5 h-3.5" /> Voltar
                  </button>
                </div>

                {/* Atirador */}
                <div className="bg-compumaq-pretoSuave/[0.03] rounded-xl p-3 mb-3">
                  <p className="text-[11px] uppercase tracking-wide text-white/35 mb-1">Atirador</p>
                  <p className="text-white font-semibold">{atirador.nome}</p>
                  <p className="text-white/40 text-xs">{formatCpf(atirador.cpf)} &middot; {atirador.clube_nome}</p>
                </div>

                {/* Evento */}
                <div className="bg-compumaq-pretoSuave/[0.03] rounded-xl p-3 mb-4">
                  <p className="text-[11px] uppercase tracking-wide text-white/35 mb-1">Evento</p>
                  <p className="text-white font-semibold">{selCampeonato.descricao}</p>
                  <p className="text-[#caa14d] text-xs">Etapa {selNumeroEtapa}</p>
                </div>

                {/* Modalidades */}
                <p className="text-[11px] uppercase tracking-wide text-white/35 mb-2">Modalidades selecionadas</p>
                <div className="space-y-2 mb-4">
                  {modalidadesComEtapa
                    .filter(m => (quantidades[m.idmodalidade] ?? 0) > 0)
                    .map(m => {
                      const qty = quantidades[m.idmodalidade]
                      const subtotal = subtotaisGlobais[m.idmodalidade] ?? 0
                      return (
                        <div
                          key={m.idmodalidade}
                          className="flex justify-between items-center bg-compumaq-pretoSuave/[0.03] rounded-lg px-3 py-2.5"
                        >
                          <div>
                            <p className="text-white text-sm font-medium">{m.nome}</p>
                            <p className="text-white/40 text-xs">
                              {m.grupo}{m.divisao ? ` · ${m.divisao}` : ''} &middot; {qty}x
                            </p>
                          </div>
                          <span className="text-emerald-400 font-semibold text-sm tabular-nums">{formatCurrency(subtotal)}</span>
                        </div>
                      )
                    })}
                </div>

                {/* Total */}
                <div className="flex justify-between items-center border-t border-white/10 pt-3">
                  <span className="text-white font-bold text-lg">Total Geral</span>
                  <span style={displayFont} className="text-emerald-400 text-3xl tabular-nums">{formatCurrency(totalGeral)}</span>
                </div>
              </div>

              {/* Admin: opcoes pos/isento direto no resumo */}
              {isAdmin && (
                <div className={PANEL}>
                  <h4 className="text-white/50 font-semibold mb-3 text-xs uppercase tracking-wide">Opcoes Admin</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => { setPaymentMode('pos'); handleFinalizarInscricao(true, 'pos') }}
                      disabled={loading}
                      className="flex flex-col items-center gap-2 p-4 rounded-xl border border-white/10 bg-compumaq-pretoSuave/[0.03] hover:bg-compumaq-pretoSuave/[0.06] hover:border-white/20 transition-all disabled:opacity-50"
                    >
                      <IconDevice className="w-6 h-6 text-white/60" />
                      <span className="text-white font-semibold text-sm">Cartao POS</span>
                      <span className="text-white/35 text-xs">Maquina no local</span>
                    </button>
                    <button
                      onClick={() => { setPaymentMode('isento'); handleFinalizarInscricao(true, 'isento') }}
                      disabled={loading}
                      className="flex flex-col items-center gap-2 p-4 rounded-xl border border-white/10 bg-compumaq-pretoSuave/[0.03] hover:bg-compumaq-pretoSuave/[0.06] hover:border-white/20 transition-all disabled:opacity-50"
                    >
                      <IconUnlock className="w-6 h-6 text-white/60" />
                      <span className="text-white font-semibold text-sm">Isento</span>
                      <span className="text-white/35 text-xs">Sem cobranca</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Botoes de pagamento para todos */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleEscolherPix}
                  disabled={loading}
                  className="flex flex-col items-center gap-2 p-5 rounded-2xl border border-white/10 bg-compumaq-pretoSuave/[0.03] hover:bg-[#caa14d]/[0.08] hover:border-[#caa14d]/40 transition-all disabled:opacity-50"
                >
                  <IconBolt className="w-7 h-7 text-[#caa14d]" />
                  <span className="text-white font-bold text-base">PIX</span>
                  <span className="text-white/40 text-xs">Instantaneo</span>
                </button>
                <button
                  onClick={handleEscolherCartao}
                  disabled={loading}
                  className="flex flex-col items-center gap-2 p-5 rounded-2xl border border-white/10 bg-compumaq-pretoSuave/[0.03] hover:bg-[#caa14d]/[0.08] hover:border-[#caa14d]/40 transition-all disabled:opacity-50"
                >
                  <IconCard className="w-7 h-7 text-[#caa14d]" />
                  <span className="text-white font-bold text-base">Cartao</span>
                  <span className="text-white/40 text-xs">Credito online</span>
                </button>
              </div>

              {loading && (
                <div className="text-center py-4">
                  <p className="text-white/40 text-sm">Criando inscricao...</p>
                </div>
              )}

              <div className="bg-compumaq-pretoSuave/[0.03] border border-white/10 rounded-xl p-3">
                <p className="text-white/40 text-xs text-center">
                  Ao clicar em PIX ou Cartao, sua inscricao sera registrada no sistema e voce sera direcionado para o pagamento.
                </p>
              </div>
            </div>
          )}

          {/* ── STEP: PAGAMENTO ──────────────────────────────────────────────── */}
          {step === 'pagamento' && atirador && selCampeonato && (
            <div className="space-y-4">
              {/* Header: inscricao criada */}
              <div className="bg-[#caa14d]/[0.08] border border-[#caa14d]/25 rounded-2xl p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#caa14d]/15 flex items-center justify-center text-[#caa14d] shrink-0">
                  <IconCheck className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-[#e8c979] font-semibold text-sm">Inscricao registrada!</p>
                  <p className="text-white/40 text-xs">Agora conclua o pagamento para confirmar.</p>
                </div>
              </div>

              {/* PIX Payment */}
              {paymentMode === 'pix' && batchResult && (
                <div className={PANEL}>
                  <div className="text-center">
                    <p className="text-white/40 text-xs mb-4">
                      Escaneie o QR Code com o aplicativo do seu banco
                    </p>

                    {qrCodeDataUrl ? (
                      <div className="inline-block bg-compumaq-pretoSuave p-3 rounded-2xl mb-4 shadow-lg">
                        <img src={qrCodeDataUrl} alt="QR Code PIX" className="w-56 h-56" />
                      </div>
                    ) : (
                      <div className="w-56 h-56 mx-auto bg-compumaq-pretoSuave/[0.05] rounded-2xl flex items-center justify-center mb-4">
                        <span className="text-white/40 text-sm">Gerando QR Code...</span>
                      </div>
                    )}

                    <p style={displayFont} className="text-emerald-400 text-3xl mb-4 tabular-nums">
                      {formatCurrency(batchResult.total)}
                    </p>

                    <div className="bg-compumaq-pretoSuave/[0.03] border border-white/10 rounded-xl p-4 mb-4 text-left">
                      <p className="text-[11px] uppercase tracking-wide text-white/35 mb-1">Chave PIX (aleatoria)</p>
                      <p className="text-white text-sm font-mono break-all mb-3 select-all">{PIX_KEY}</p>
                      <button
                        onClick={copiarChavePix}
                        className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-all ${
                          pixCopiado ? 'bg-emerald-600 text-white' : 'bg-compumaq-pretoSuave/[0.06] hover:bg-compumaq-pretoSuave/[0.12] text-white/80'
                        }`}
                      >
                        {pixCopiado ? '✓ Chave copiada!' : 'Copiar Chave PIX'}
                      </button>
                    </div>

                    <div className="bg-amber-950/40 border-2 border-amber-500/40 rounded-xl p-4 mb-4 text-left">
                      <p className="text-amber-400 font-black text-sm mb-2 uppercase tracking-wide">
                        Aviso importante
                      </p>
                      <p className="text-amber-200/90 text-sm mb-3">
                        Apos realizar o pagamento, envie o comprovante do PIX para o WhatsApp:
                      </p>
                      <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3 px-4 rounded-xl transition-colors text-sm"
                      >
                        <IconWhatsApp />
                        Enviar comprovante: (66) 99210-7472
                      </a>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 mt-2">
                    <button
                      onClick={() => { setBatchResult(batchResult); setStep('sucesso') }}
                      className={`${BTN_PRIMARY} w-full py-4 text-lg shadow-lg shadow-black/40`}
                    >
                      Ja enviei o comprovante
                    </button>
                    <p className="text-white/30 text-xs text-center">
                      Sua inscricao esta registrada como <span className="text-amber-400">pendente de pagamento</span>. Sera confirmada apos verificacao do PIX.
                    </p>
                  </div>
                </div>
              )}

              {/* Cartao Payment */}
              {paymentMode === 'cartao' && (
                <div className={PANEL}>
                  {cartaoErro && (
                    <div className="mb-4 bg-red-950/50 border border-red-500/30 rounded-xl p-3 text-red-300 text-sm flex justify-between items-start gap-2">
                      <span>{cartaoErro}</span>
                      <button className="text-red-400 hover:text-red-200 shrink-0" onClick={() => setCartaoErro(null)}>
                        <IconClose />
                      </button>
                    </div>
                  )}

                  <form onSubmit={handleCartaoSubmit} className="space-y-3.5">
                    <div>
                      <label className={LABEL}>Numero do cartao *</label>
                      <input
                        type="text" inputMode="numeric" autoComplete="cc-number" maxLength={19}
                        placeholder="0000  0000  0000  0000"
                        value={cartaoForm.numero}
                        onChange={e => setCartaoForm(p => ({ ...p, numero: maskCardNumber(e.target.value) }))}
                        required
                        className={`${INPUT} font-mono tracking-widest`}
                      />
                    </div>
                    <div>
                      <label className={LABEL}>Nome no cartao *</label>
                      <input
                        type="text" autoComplete="cc-name" placeholder="Igual ao impresso no cartao"
                        value={cartaoForm.nome}
                        onChange={e => setCartaoForm(p => ({ ...p, nome: e.target.value.toUpperCase() }))}
                        required
                        className={`${INPUT} uppercase`}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3.5">
                      <div>
                        <label className={LABEL}>Validade *</label>
                        <input
                          type="text" inputMode="numeric" autoComplete="cc-exp" maxLength={5} placeholder="MM/AA"
                          value={cartaoForm.validade}
                          onChange={e => setCartaoForm(p => ({ ...p, validade: maskValidade(e.target.value) }))}
                          required
                          className={`${INPUT} font-mono`}
                        />
                      </div>
                      <div>
                        <label className={LABEL}>CVV *</label>
                        <input
                          type="text" inputMode="numeric" autoComplete="cc-csc" maxLength={4} placeholder="123"
                          value={cartaoForm.cvv}
                          onChange={e => setCartaoForm(p => ({ ...p, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                          required
                          className={`${INPUT} font-mono`}
                        />
                      </div>
                    </div>
                    <div>
                      <label className={LABEL}>CPF do titular *</label>
                      <input
                        type="text" inputMode="numeric" maxLength={14} placeholder="000.000.000-00"
                        value={cartaoForm.cpf_titular}
                        onChange={e => setCartaoForm(p => ({ ...p, cpf_titular: maskCpf(e.target.value) }))}
                        required
                        className={`${INPUT} font-mono`}
                      />
                    </div>
                    <div>
                      <label className={LABEL}>Parcelas</label>
                      <select
                        value={cartaoForm.parcelas}
                        onChange={e => setCartaoForm(p => ({ ...p, parcelas: parseInt(e.target.value, 10) }))}
                        className={SELECT}
                      >
                        {calcularParcelasOpcoes(totalGeral).map(op => (
                          <option key={op.n} value={op.n}>{op.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="bg-compumaq-pretoSuave/[0.03] rounded-xl p-3 flex justify-between items-center">
                      <div>
                        <span className="text-white/60 text-sm">Total a pagar</span>
                        {cartaoForm.parcelas > 1 && (
                          <p className="text-white/30 text-xs mt-0.5">Total s/ juros: {formatCurrency(totalGeral)}</p>
                        )}
                      </div>
                      <span className="text-emerald-400 font-black text-xl tabular-nums">
                        {formatCurrency(calcularValorComJuros(totalGeral, cartaoForm.parcelas))}
                      </span>
                    </div>
                    <div className="flex gap-3 pt-1">
                      <button
                        type="button"
                        onClick={() => setStep('resumo')}
                        className={`${BTN_GHOST} flex-none px-4 py-3 text-sm flex items-center gap-1`}
                      >
                        <IconArrowLeft className="w-3.5 h-3.5" /> Voltar
                      </button>
                      <button
                        type="submit" disabled={cartaoLoading}
                        className={`${BTN_PRIMARY} flex-1 py-3 text-base`}
                      >
                        {cartaoLoading ? 'Processando...' : `Pagar ${formatCurrency(calcularValorComJuros(totalGeral, cartaoForm.parcelas))}`}
                      </button>
                    </div>
                  </form>
                  <p className="text-white/25 text-xs text-center mt-3">
                    Pagamento processado com seguranca via PagBank. Seus dados de cartao nao trafegam pelo nosso servidor.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ── STEP: SUCESSO ─────────────────────────────────────────────────── */}
          {step === 'sucesso' && batchResult && atirador && selCampeonato && (
            <div className={`${PANEL} !border-emerald-500/25`}>
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-4 text-emerald-400">
                  <IconCheck className="w-7 h-7" />
                </div>
                <h2 style={displayFont} className="text-2xl sm:text-3xl uppercase tracking-wide text-emerald-400 mb-1">
                  Inscricao Realizada!
                </h2>
                <p className="text-white/40 text-sm">
                  {cartaoAprovado
                    ? 'Pagamento aprovado e inscricoes confirmadas.'
                    : posIsento
                    ? 'Inscricao confirmada com sucesso.'
                    : 'Suas inscricoes foram registradas com sucesso.'}
                </p>
                {cartaoAprovado && (
                  <span className="inline-flex items-center gap-1.5 mt-2 bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs px-3 py-1 rounded-full font-semibold">
                    <IconCard className="w-3.5 h-3.5" /> Pagamento via cartao aprovado
                  </span>
                )}
                {posIsento && !cartaoAprovado && (
                  <span className="inline-flex items-center gap-1.5 mt-2 bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs px-3 py-1 rounded-full font-semibold">
                    <IconCheck className="w-3.5 h-3.5" /> Inscricao confirmada pelo administrador
                  </span>
                )}
              </div>

              <div className="bg-compumaq-pretoSuave/[0.03] rounded-xl p-4 mb-4 space-y-3">
                <div className="flex justify-between items-start border-b border-white/10 pb-3">
                  <span className="text-white/40 text-sm">Atirador</span>
                  <div className="text-right">
                    <p className="text-white text-sm font-semibold">{atirador.nome}</p>
                    <p className="text-white/40 text-xs">{atirador.clube_nome}</p>
                  </div>
                </div>
                <div className="flex justify-between items-start border-b border-white/10 pb-3">
                  <span className="text-white/40 text-sm">Evento</span>
                  <div className="text-right">
                    <p className="text-white text-sm">{selCampeonato.descricao}</p>
                    <p className="text-[#caa14d] text-xs">Etapa {selNumeroEtapa}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {batchResult.inscricoes.map((insc, idx) => (
                    <div key={idx} className="flex justify-between items-center">
                      <div>
                        <p className="text-white/70 text-sm">{insc.modalidade_nome}</p>
                        <p className="text-white/35 text-xs">{insc.quantidade}x inscricao</p>
                      </div>
                      <span className="text-emerald-400 text-sm font-semibold tabular-nums">{formatCurrency(insc.subtotal)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center border-t border-white/10 pt-3">
                  <span className="text-white font-bold">Total</span>
                  <span className="text-emerald-400 font-black text-xl tabular-nums">{formatCurrency(batchResult.total)}</span>
                </div>
              </div>

              {/* Lembrete PIX (somente se nao pagou por cartao nem foi confirmado como pos/isento) */}
              {!cartaoAprovado && !posIsento && (
                <div className="bg-amber-950/40 border border-amber-500/25 rounded-xl p-4 mb-6">
                  <p className="text-amber-300 font-bold text-sm mb-2">Lembrete de Pagamento</p>
                  <p className="text-amber-200/90 text-sm mb-3">
                    Realize o pagamento via PIX e envie o comprovante para o WhatsApp:
                  </p>
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-2.5 px-4 rounded-xl transition-colors text-sm"
                  >
                    <IconWhatsApp className="w-4 h-4" />
                    Enviar comprovante: (66) 99210-7472
                  </a>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={resetar}
                  className={`${BTN_GHOST} flex-1 py-3 text-sm`}
                >
                  Nova Inscricao
                </button>
                <button
                  onClick={imprimirComprovante}
                  className={`${BTN_GHOST} flex-1 py-3 text-sm`}
                >
                  Salvar e Imprimir
                </button>
                <Link
                  to="/"
                  className={`${BTN_PRIMARY} flex-1 py-3 text-sm text-center`}
                >
                  Voltar ao Inicio
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
